import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getScoringRules, createScoringRule, updateScoringRule, type ScoringRule, type ScoringRuleKind } from '@/services'
import { AppButton, EmptyState, Modal, IconButton, Skeleton, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

type UiType = 'event' | 'page' | 'campaign' | 'frequency' | 'recency' | 'conversion'

const RULE_TYPES: Array<{ value: UiType; label: string; icon: string }> = [
  { value: 'event', label: 'Evento', icon: 'zap' },
  { value: 'page', label: 'Página vista', icon: 'eye' },
  { value: 'campaign', label: 'Campaña (UTM)', icon: 'flag' },
  { value: 'frequency', label: 'Frecuencia', icon: 'refreshCw' },
  { value: 'recency', label: 'Recencia', icon: 'activity' },
  { value: 'conversion', label: 'Conversión', icon: 'check' },
]

const EVENT_OPTIONS = [
  { value: 'checkout_started', label: 'Empezó checkout' },
  { value: 'checkout_abandoned', label: 'Abandonó checkout' },
  { value: 'signup_completed', label: 'Completó signup' },
  { value: 'pricing_viewed', label: 'Vio pricing' },
  { value: 'demo_requested', label: 'Pidió demo' },
  { value: 'fast_bounce', label: 'Rebote rápido (< 10s)' },
]

function kindToType(kind: ScoringRuleKind): UiType {
  if (kind === 'page_view') return 'page'
  if (kind === 'utm') return 'campaign'
  return kind as UiType
}
function typeToKind(type: UiType): ScoringRuleKind {
  if (type === 'page') return 'page_view'
  if (type === 'campaign') return 'utm'
  return type as ScoringRuleKind
}

function humanFor(rule: ScoringRule): string {
  const m = rule.match || {}
  switch (rule.kind) {
    case 'event': return `evento "${m.event_type || '?'}"`
    case 'page_view': return `visita una página que contiene "${m.path_pattern || '%'}"`
    case 'utm': {
      const parts: string[] = []
      if (m.utm_source) parts.push(`utm_source = "${m.utm_source}"`)
      if (m.utm_medium) parts.push(`utm_medium = "${m.utm_medium}"`)
      if (m.utm_campaign) parts.push(`utm_campaign = "${m.utm_campaign}"`)
      return parts.join(' y ') || 'cualquier UTM'
    }
    case 'frequency': return `mínimo ${m.min_sessions || 1} sesiones`
    case 'recency': return `visitó en los últimos ${m.within_days || 7} días`
    case 'conversion': return 'subscription pagada'
    default: return ''
  }
}

export function ScoringPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<ScoringRule | null | 'new'>(null)

  const { data: result, isLoading } = useQuery({ queryKey: ['scoring-rules'], queryFn: () => getScoringRules() })
  const rules = result?.data || []
  const activeCount = rules.filter((r) => r.is_active).length

  const toggleMutation = useMutation({
    mutationFn: (r: ScoringRule) => updateScoringRule(r.id, { is_active: !r.is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scoring-rules'] }),
    onError: (e: any) => toast.error(e?.message || 'No se pudo actualizar la regla'),
  })

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Marketing</div>
          <h1>Reglas de scoring</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={380} height={14} /> : `${rules.length} reglas · ${activeCount} activas. Las reglas definen cómo se reparten los puntos a cada contacto.`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="plus" onClick={() => setEditing('new')}>Nueva regla</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <TableSkeleton columns={7} rows={8} />
        ) : rules.length === 0 ? (
          <EmptyState icon="zap" title="Todavía no hay reglas" desc="Creá la primera para empezar a puntuar contactos." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th style={{ width: 130 }}>Tipo</th>
                <th>Condición</th>
                <th style={{ textAlign: 'right', width: 80 }}>Puntos</th>
                <th style={{ textAlign: 'right', width: 60 }}>Tope</th>
                <th style={{ width: 100 }}>Estado</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => {
                const type = kindToType(r.kind)
                const typeDef = RULE_TYPES.find((t) => t.value === type)
                return (
                  <tr key={r.id} onClick={() => setEditing(r)} {...rowActivation(() => setEditing(r))}>
                    <td>
                      <div className="cell-primary">{r.name}</div>
                      {r.description ? <div className="cell-secondary">{r.description}</div> : null}
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>
                        <Icon name={typeDef?.icon || 'zap'} size={13} />{typeDef?.label || r.kind}
                      </span>
                    </td>
                    <td><span className="condition-pill">{humanFor(r)}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, fontVariantNumeric: 'tabular-nums', color: r.points >= 0 ? 'var(--success-fg)' : 'var(--danger-fg)' }}>{(r.points >= 0 ? '+' : '') + r.points}</span>
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>{r.max_per_contact ? `${r.max_per_contact}×` : '∞'}</td>
                    <td>
                      <label className="toggle" onClick={(e) => { e.stopPropagation(); toggleMutation.mutate(r) }}>
                        <input type="checkbox" checked={r.is_active} readOnly />
                        <span style={{ fontSize: 12, color: r.is_active ? 'var(--text)' : 'var(--text-3)' }}>{r.is_active ? 'Activa' : 'Inactiva'}</span>
                      </label>
                    </td>
                    <td><IconButton icon="moreVertical" tooltip="Acciones" size={14} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing ? (
        <RuleFormModal
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['scoring-rules'] }); setEditing(null) }}
        />
      ) : null}
    </>
  )
}

function RuleFormModal({ rule, onClose, onSaved }: { rule: ScoringRule | null; onClose: () => void; onSaved: () => void }) {
  const m = rule?.match || {}
  const [name, setName] = useState(rule?.name || '')
  const [description, setDescription] = useState(rule?.description || '')
  const [type, setType] = useState<UiType>(rule ? kindToType(rule.kind) : 'event')
  const [eventName, setEventName] = useState(String(m.event_type || 'checkout_started'))
  const [pagePattern, setPagePattern] = useState(String(m.path_pattern || '%pricing%'))
  const [utmSource, setUtmSource] = useState(String(m.utm_source || ''))
  const [utmMedium, setUtmMedium] = useState(String(m.utm_medium || ''))
  const [utmCampaign, setUtmCampaign] = useState(String(m.utm_campaign || ''))
  const [minSessions, setMinSessions] = useState(Number(m.min_sessions || 3))
  const [recentDays, setRecentDays] = useState(Number(m.within_days || 7))
  const [points, setPoints] = useState(String(rule?.points ?? 10))
  const [cap, setCap] = useState(String(rule?.max_per_contact ?? 1))
  const [active, setActive] = useState(rule?.is_active ?? true)

  function buildMatch(): Record<string, unknown> {
    switch (type) {
      case 'event': return { event_type: eventName }
      case 'page': return { path_pattern: pagePattern }
      case 'campaign': return { utm_source: utmSource || undefined, utm_medium: utmMedium || undefined, utm_campaign: utmCampaign || undefined }
      case 'frequency': return { min_sessions: minSessions }
      case 'recency': return { within_days: recentDays }
      default: return {}
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        kind: typeToKind(type),
        match: buildMatch(),
        points: parseInt(points, 10) || 0,
        max_per_contact: parseInt(cap, 10) || null,
        is_active: active,
      }
      return rule ? updateScoringRule(rule.id, body) : createScoringRule(body)
    },
    onSuccess: () => { toast.success(rule ? 'Regla actualizada' : 'Regla creada'); onSaved() },
    onError: (e: any) => toast.error(e?.message || 'No se pudo guardar la regla'),
  })

  const pointsNum = parseInt(points, 10) || 0
  const capNum = parseInt(cap, 10) || 0

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={rule ? 'Editar regla' : 'Nueva regla de scoring'}
      subtitle="Las reglas se evalúan en orden y aplican al recalcular."
      footer={
        <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
          <AppButton variant="ghost" onClick={onClose}>Cancelar</AppButton>
          <AppButton variant="success" icon="check" disabled={!name.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>{rule ? 'Guardar cambios' : 'Crear regla'}</AppButton>
        </div>
      }
    >
      <div className="col gap-4">
        <div className="form-row">
          <label htmlFor="sc-name">Nombre</label>
          <input id="sc-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="ej. Vio pricing" />
        </div>
        <div className="form-row">
          <label htmlFor="sc-desc">Descripción interna</label>
          <input id="sc-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Para qué sirve esta regla, en una línea." />
        </div>

        <div className="form-row">
          <label id="sc-type-label">Tipo</label>
          <div role="group" aria-labelledby="sc-type-label" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {RULE_TYPES.map((opt) => (
              <button key={opt.value} type="button" onClick={() => setType(opt.value)} style={{ padding: '10px 12px', borderRadius: 'var(--r-3)', border: '1px solid ' + (type === opt.value ? 'var(--bisne-green)' : 'var(--border-2)'), background: type === opt.value ? 'var(--success-bg)' : 'var(--surface)', color: type === opt.value ? 'var(--success-fg)' : 'var(--text-2)', fontWeight: 500, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <Icon name={opt.icon} size={14} />{opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '14px 16px', background: 'var(--surface-2)', borderRadius: 'var(--r-3)', border: '1px solid var(--border)' }}>
          <div className="t-eyebrow" style={{ marginBottom: 10 }}>Condición</div>
          {type === 'event' ? (
            <div className="form-row">
              <label htmlFor="sc-event">¿Qué evento dispara la regla?</label>
              <select id="sc-event" className="select" value={eventName} onChange={(e) => setEventName(e.target.value)}>
                {EVENT_OPTIONS.map((e) => <option key={e.value} value={e.value}>{e.label} ({e.value})</option>)}
              </select>
            </div>
          ) : null}
          {type === 'page' ? (
            <div className="form-row">
              <label htmlFor="sc-page">Patrón de ruta</label>
              <input id="sc-page" className="input" value={pagePattern} onChange={(e) => setPagePattern(e.target.value)} placeholder="%pricing%" style={{ fontFamily: 'var(--font-mono)' }} />
              <span className="hint">Usá % como comodín. Ej: %/pricing%, /casos%</span>
            </div>
          ) : null}
          {type === 'campaign' ? (
            <>
              <div className="form-row"><label htmlFor="sc-utm-source">utm_source</label><input id="sc-utm-source" className="input" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="ej. google" style={{ fontFamily: 'var(--font-mono)' }} /></div>
              <div className="form-row"><label htmlFor="sc-utm-medium">utm_medium</label><input id="sc-utm-medium" className="input" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="ej. cpc" style={{ fontFamily: 'var(--font-mono)' }} /></div>
              <div className="form-row"><label htmlFor="sc-utm-campaign">utm_campaign</label><input id="sc-utm-campaign" className="input" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="ej. mayo-launch" style={{ fontFamily: 'var(--font-mono)' }} /></div>
              <div className="hint" style={{ fontSize: 11.5 }}>Dejá vacíos los campos que no querés filtrar.</div>
            </>
          ) : null}
          {type === 'frequency' ? (
            <div className="form-row"><label htmlFor="sc-minsessions">Mínimo de sesiones</label><input id="sc-minsessions" className="input" type="number" min={1} value={minSessions} onChange={(e) => setMinSessions(parseInt(e.target.value, 10) || 1)} /></div>
          ) : null}
          {type === 'recency' ? (
            <div className="form-row"><label htmlFor="sc-recentdays">Visitó en los últimos N días</label><input id="sc-recentdays" className="input" type="number" min={1} value={recentDays} onChange={(e) => setRecentDays(parseInt(e.target.value, 10) || 1)} /></div>
          ) : null}
          {type === 'conversion' ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Sin campos adicionales. La regla se aplica cuando el contacto pague su subscription.</div>
          ) : null}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <div className="form-row"><label htmlFor="sc-points">Puntos</label><input id="sc-points" className="input" type="number" value={points} onChange={(e) => setPoints(e.target.value)} /><span className="hint">Negativos restan. Ej: -5</span></div>
          <div className="form-row"><label htmlFor="sc-cap">Tope por contacto</label><input id="sc-cap" className="input" type="number" min={0} value={cap} onChange={(e) => setCap(e.target.value)} /><span className="hint">0 = sin tope.</span></div>
          <div className="form-row"><label>Estado</label><label className="toggle" style={{ marginTop: 8 }}><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /><span>{active ? 'Activa' : 'Inactiva'}</span></label></div>
        </div>

        <div style={{ padding: '12px 14px', background: 'var(--bisne-dark)', color: 'var(--text-inverse)', borderRadius: 'var(--r-3)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <Icon name="eye" size={14} style={{ opacity: 0.6 }} />
          <div style={{ flex: 1, fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-inverse-2)' }}>Resumen: </span>
            Cuando <strong>{humanFor({ kind: typeToKind(type), match: buildMatch() } as ScoringRule)}</strong>, sumar{' '}
            <strong style={{ color: pointsNum >= 0 ? 'var(--bisne-green)' : 'var(--bisne-orange)' }}>{(pointsNum >= 0 ? '+' : '') + pointsNum + ' pts'}</strong>
            {capNum > 0 ? ` (máx ${capNum}×)` : '.'}
          </div>
        </div>
      </div>
    </Modal>
  )
}
